/**
 * Treatment detail bottom sheet — S37 shared module (was the
 * home-featured-treatments view.js until the treatments wizard step 1 grew
 * the same Details → sheets; one implementation now serves every surface).
 *
 * Vanilla JS, no jQuery / no React. The sheets are server-rendered (one
 * hidden `.cleo-treat__overlay` dialog per card, buffered after the grid by
 * inc/detail-sheet.php); this module only opens and closes them, so the
 * no-JS DOM stays intact: every trigger keeps a real href and the sheet's
 * Book & Pay is a real link to /treatments/?book={slug}.
 *
 * Port of the amended mockup's openDetail / closeDetail / stopClose:
 *   - clicking a card trigger opens that card's sheet instead of navigating;
 *   - the × button, a click on the scrim outside the sheet, and Escape close
 *     it and return focus to the trigger that opened it;
 *   - clicks inside the sheet panel never close it (stopClose).
 *
 * Body scroll locks while a sheet is open; the cleoFade entrance runs from
 * CSS because display:none → flex restarts the keyframe.
 *
 * S38: the scroll lock is triple-belted — inline overflow:hidden on html AND
 * body, plus a wheel guard that swallows every wheel not aimed at the sheet
 * body (the sheet's only scrollport). Without the guard a wheel over the
 * dark scrim still moved content, because any scrollport under the cursor
 * takes the event before the document lock matters.
 */

const TRIGGER = "[data-cleo-sheet]";
const CLOSER = "[data-cleo-sheet-close]";
const OVERLAY = ".cleo-treat__overlay";

let lastTrigger = null;
let openEl = null;

// S38: the sheet body is the one scrollport allowed to answer a wheel.
// Everything else — scrim, sheet head, anything behind the fixed overlay —
// is swallowed so the page behind can never move while a sheet is open.
function onWheel(event) {
	if (!openEl) {
		return;
	}

	const scroller = openEl.querySelector(".cleo-detail__body");

	if (scroller && scroller.contains(event.target)) {
		return; // native scroll inside the sheet; overscroll-behavior contains it
	}

	event.preventDefault();
}

function lockScroll() {
	document.documentElement.style.overflow = "hidden";
	document.body.style.overflow = "hidden";
	document.addEventListener("wheel", onWheel, { passive: false });
}

function unlockScroll() {
	document.documentElement.style.overflow = "";
	document.body.style.overflow = "";
	document.removeEventListener("wheel", onWheel);
}

function openSheet(sheet, trigger) {
	lastTrigger = trigger;
	openEl = sheet;
	sheet.hidden = false;
	sheet.scrollTop = 0; // always open at the sheet's title, never mid-sheet
	const body = sheet.querySelector(".cleo-detail__body");
	if (body) {
		body.scrollTop = 0;
	}
	lockScroll();

	const closer = sheet.querySelector(CLOSER);

	if (closer) {
		// preventScroll: the × sits at the sheet top, in view at scrollTop 0.
		// A plain focus() lets the browser chase the button through every
		// scrollport — including the document — and nudges the page behind
		// the scrim (S35: the jump-to-footer the user reported).
		closer.focus({ preventScroll: true });
	}
}

function closeSheet(sheet) {
	sheet.hidden = true;
	openEl = null;
	unlockScroll();

	if (lastTrigger && document.contains(lastTrigger)) {
		lastTrigger.focus();
	}

	lastTrigger = null;
}

export function initDetailSheet() {
	document.addEventListener("click", (event) => {
		const trigger = event.target.closest(TRIGGER);

		if (trigger) {
			const sheet = document.getElementById(trigger.getAttribute("data-cleo-sheet"));

			// No sheet in the DOM (query edge) → let the link navigate.
			if (sheet) {
				event.preventDefault();
				openSheet(sheet, trigger);
			}

			return;
		}

		const closer = event.target.closest(CLOSER);

		if (closer) {
			const owner = closer.closest(OVERLAY);

			if (owner) {
				closeSheet(owner);
			}

			return;
		}

		// Scrim click closes; anything inside the sheet panel is stopClose.
		const overlay = event.target.closest(OVERLAY);

		if (overlay && event.target === overlay) {
			closeSheet(overlay);
		}
	});

	document.addEventListener("keydown", (event) => {
		if ("Escape" !== event.key && "Esc" !== event.key) {
			return;
		}

		const openSheetEl = document.querySelector(OVERLAY + ":not([hidden])");

		if (openSheetEl) {
			closeSheet(openSheetEl);
		}
	});
}
